import { useEffect, useState } from 'react';

import type { Eventual } from '../eventual';
import { useRuntime } from '../internal';
import type { LoadedXinglet } from '../xinglet';

export type AsyncXinglet =
  | { loading: true; xinglet: undefined; error: undefined }
  | { loading: false; xinglet: LoadedXinglet; error: undefined }
  | { loading: false; xinglet: undefined; error: Error }
  | { loading: false; xinglet: undefined; error: undefined };

function wrapError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function asyncXinglet(
  eventual: Eventual<LoadedXinglet>,
  skip = false
): AsyncXinglet {
  if (skip) return { loading: false, xinglet: undefined, error: undefined };

  return eventual.error
    ? { loading: false, xinglet: undefined, error: wrapError(eventual.error) }
    : eventual.settled
      ? { loading: false, xinglet: eventual.value, error: undefined }
      : { loading: true, xinglet: undefined, error: undefined };
}

export function useXinglet(name: string): AsyncXinglet {
  const runtime = useRuntime();
  const skip = !runtime.metadataMap[name]?.ssr && runtime.isServer;
  const eventualXinglet = runtime.loadXinglet(name);
  const [result, setResult] = useState(asyncXinglet(eventualXinglet, skip));

  useEffect(() => {
    if (!result.loading) return;

    eventualXinglet.then(
      () => setResult(asyncXinglet(eventualXinglet)),
      () => setResult(asyncXinglet(eventualXinglet))
    );
  });

  return result;
}

export function useXinglets(names: string[]): { loading: boolean } {
  const runtime = useRuntime();

  const eventualXinglets = names.map((name) => {
    return runtime.loadXinglet(name);
  });

  const [ready, setReady] = useState(
    eventualXinglets.every((eventual) => eventual.settled)
  );

  if (!ready) {
    Promise.all(eventualXinglets).then(() => {
      setReady(true);
    });
  }

  return { loading: !ready };
}
